import { useEffect, useState, useRef } from 'react';
import Swal from 'sweetalert2';
import { fetchScenario, postMarkerFind, finishSession } from '../../services/api';
import { renderBodyWithMarkers } from '../../utils/renderBody';
import './EmailSimulation.css';


function EmailSimulation({ player, scenarioId, scenarioIndex, totalScenarios, onNext, onBackToMenu }) {
  const [scenario, setScenario] = useState(null);
  const [error, setError] = useState(null);
  const [foundLocations, setFoundLocations] = useState([]);
  const [foundIds, setFoundIds] = useState([]);
  const [wrongClicks, setWrongClicks] = useState(0);
  const [result, setResult] = useState(null);
  const [shownHints, setShownHints] = useState(0);
  const startTime = useRef(Date.now());
  const [completedDifficulties, setCompletedDifficulties] = useState(new Set());

  const showFinalCertificate = (player, onBackToMenu) => {
  Swal.fire({
    title: '🏆 Phishing-Profi!',
    html: `
      <p style="font-size: 16px; margin-top: 10px;">
        Herzlichen Glückwunsch, <strong>${player.name}</strong>!
      </p>
      <p style="color: #555;">
        Du hast alle Schwierigkeitsstufen (Basic, Fortgeschritten & Experte) gemeistert und 
        <strong style="color: #6BB937; font-size: 20px;">${player.total_points} Punkte</strong> erzielt.
      </p>
    `,
    icon: 'success',
    confirmButtonText: 'Zurück zum Hauptmenü 🏠',
    confirmButtonColor: '#6BB937',
    allowOutsideClick: false,
  }).then(() => {
    onBackToMenu();
  });
};

  useEffect(() => {
    setScenario(null);
    setFoundLocations([]);
    setFoundIds([]);
    setWrongClicks(0);
    setResult(null);
    setShownHints(0);
    startTime.current = Date.now();
    fetchScenario(scenarioId)
      .then(setScenario)
      .catch((err) => setError(err.message));
  }, [scenarioId]);

  useEffect(() => {
  if (result && scenario?.difficulty) {
    setCompletedDifficulties((prev) => new Set(prev).add(scenario.difficulty.toLowerCase()));
  }
}, [result, scenario]);

  const personalizeBody = (body) => {
    if (!body) return body;
    return body.replace('{{user_firstname}}', player.name);
  };

  const handleClick = async (location) => {
    if (result) return;
    if (foundLocations.includes(location)) return;

    const markerResult = await postMarkerFind({
      scenario_id: scenario.id,
      clicked_location: location,
    });

    if (markerResult.was_correct) {
      const newFoundIds = [...foundIds, markerResult.marker_id];
      setFoundIds(newFoundIds);
      setFoundLocations((prev) => [...prev, location]);
      const marker = scenario.error_markers.find((m) => m.location === location);

      const isAllFound = newFoundIds.length === scenario.error_markers.length;

      Swal.fire({
        title: 'Richtig erkannt!',
        text: marker?.explanation || 'Fehlerstelle erfolgreich gefunden.',
        icon: 'success',
        confirmButtonText: 'Weiter',
        confirmButtonColor: '#6BB937',
      }).then(() => {
        if (isAllFound) {
          handleFinish(newFoundIds.length, wrongClicks);
        }
      });

    } else {
      const newWrongClicks = wrongClicks + 1;
      setWrongClicks(newWrongClicks);

      Swal.fire({
        title: 'Kein Fehler!',
        text: 'Das ist keine Fehlerstelle. Schau noch einmal genau hin.',
        icon: 'error',
        confirmButtonText: 'Verstanden',
        confirmButtonColor: '#f44336',
      });
    }
  };

  const handleShowHint = () => {
    if (shownHints < (scenario.hints?.length || 0)) {
      const nextHint = scenario.hints[shownHints];
      setShownHints((prev) => prev + 1);

      Swal.fire({
        title: `Hinweis ${shownHints + 1}/${scenario.hints.length}`,
        text: nextHint.text,
        icon: 'info',
        confirmButtonText: 'Alles klar',
        confirmButtonColor: '#3085d6',
      });
    }
  };

  const handleFinish = async (correctCount, wrongCount) => {
    const durationSeconds = Math.floor((Date.now() - startTime.current) / 1000);
    try {
      const sessionResult = await finishSession({
        scenario_id: scenario.id,
        correct_clicks: correctCount,
        wrong_clicks: wrongCount,
        duration_seconds: durationSeconds,
      });
      setResult(sessionResult);
    } catch (err) {
      console.error('Fehler beim Beenden der Session:', err);
    }
  };

  if (error) return <p>Fehler: {error}</p>;
  if (!scenario) return <p>Lade Szenario...</p>;

  if (result) {
    return (
      <div className="result-container">
        <h2>Auswertung</h2>
        <p style={{ color: '#666', marginTop: '4px' }}>
          Szenario {scenarioIndex + 1} von {totalScenarios}
        </p>
        <div className="result-grid">
          <div className="result-item">
            <p className="label">Richtige Klicks</p>
            <p className="value" style={{ color: '#6BB937' }}>{result.session.correct_clicks}</p>
          </div>
          <div className="result-item">
            <p className="label">Falsche Klicks</p>
            <p className="value" style={{ color: result.session.wrong_clicks > 0 ? '#f44336' : '#6BB937' }}>
              {result.session.wrong_clicks}
            </p>
          </div>
          <div className="result-item">
            <p className="label">Punkte diese Runde</p>
            <p className="value">{result.pointsEarned}</p>
          </div>
          <div className="result-item">
            <p className="label">Gesamtpunkte</p>
            <p className="value">{result.player.total_points}</p>
          </div>
        </div>
        <div className="result-actions">
          <button 
  className="btn-primary" 
  onClick={() => {
    // Alle geforderten Schwierigkeitsgrade definieren
    const requiredDifficulties = ['basic', 'fortgeschritten', 'experte'];
    
    // Prüfen, ob nach diesem Szenario alle 3 Typen mindestens einmal gespielt wurden
    const currentCompleted = new Set(completedDifficulties).add(scenario.difficulty.toLowerCase());
    const hasCompletedAllLevels = requiredDifficulties.every((level) => currentCompleted.has(level));

    const isLastScenarioInList = scenarioIndex + 1 >= totalScenarios;

    // Pop-up erscheint nur, wenn ALLE Schwierigkeitsgrade durchgespielt sind ODER alle Szenarien beendet wurden
    if (hasCompletedAllLevels || isLastScenarioInList) {
      showFinalCertificate(result.player, onBackToMenu);
    } else {
      onNext(result.player);
    }
  }}
>
  {scenarioIndex + 1 < totalScenarios ? 'Nächstes Szenario →' : 'Abschließen'}
</button>
        </div>
      </div>
    );
  }

  const hints = scenario.hints || [];

  return (
    <div className="simulation-container">
      <div className="simulation-progress">
        Phishing-Quiz - Szenario {scenarioIndex + 1} von {totalScenarios}: <strong>{scenario.title}</strong>
        <span className={`difficulty-badge difficulty-${scenario.difficulty}`}>{scenario.difficulty}</span>
      </div>

      <div className="email-client">
        <div className="email-client-header">
          <span>📧 E-Mail-Posteingang</span>
        </div>

        <div className="email-meta">
          <div className="email-meta-row">
            <span className="email-meta-label">Von:</span>
            <span onClick={() => handleClick('sender')} style={{ cursor: 'default' }}>
              {scenario.sender}
            </span>
          </div>
          <div className="email-meta-row">
            <span className="email-meta-label">Betreff:</span>
            <span onClick={() => handleClick('subject')} style={{ cursor: 'default' }}>
              {scenario.subject}
            </span>
          </div>
        </div>

        <div
          className="email-body"
          style={{ whiteSpace: 'pre-line', cursor: 'default' }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              handleClick('body_text');
            }
          }}
        >
          {renderBodyWithMarkers(personalizeBody(scenario.body), scenario.error_markers, handleClick)}
        </div>

        <div className="email-signature">
          <hr className="signature-divider" />
          <img
            src={`/${scenario.signature_image}`}
            alt="Signatur"
            className={`signature-img ${scenario.signature_is_fake ? 'clickable-signature' : ''}`}
            onClick={() => scenario.signature_is_fake && handleClick('signature')}
          />
        </div>
      </div>

      <div className="simulation-footer">
        <span className="found-counter">
          🔍 Gefundene Fehler: {foundIds.length}/{scenario.error_markers.length}
        </span>
        {hints.length > 0 && (
          <button
            className="btn-secondary"
            style={{ fontSize: '13px', padding: '4px 10px' }}
            onClick={handleShowHint}
            disabled={shownHints >= hints.length}
          >
            💡 Hinweis {shownHints}/{hints.length}
          </button>
        )}
        <span className="wrong-counter">
          ❌ Fehlklicks: {wrongClicks}
        </span>
      </div>

      <button
        className="btn-secondary"
        style={{ marginTop: '8px', fontSize: '13px' }}
        onClick={onBackToMenu}
      >
        ← Zurück zum Menü
      </button>
    </div>
  );
}

export default EmailSimulation;